import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { StateService } from "@uirouter/angular";
import { PersonService } from "../services/person.service";
import { WorkplaceService } from "../services/workplace.service";
import { WorkgroupService } from "../services/workgroup.service";
import { ActivityTypeService } from "../services/activity-type.service";
import { Observable, Subject } from "rxjs";
import { ActivityService } from "../services/activity.service";
import { takeUntil, switchMap, map, tap } from "rxjs/operators";
import { CrewPermissionsService } from "../core/crew-permissions.service";
import { HttpResponse } from "@angular/common/http";

import * as dataCore from "../core/datacore";

@Component({
    selector: "clear-activities",
    template: require('./clear-activities.component.html')
})
export class ClearActivities implements OnInit, OnDestroy {

    constructor(
        private personService: PersonService,
        private workplaceService: WorkplaceService,
        private workgroupService: WorkgroupService,
        private activityTypeService: ActivityTypeService,
        private activityService: ActivityService,
        private stateService: StateService,
        private crewPermissionsService: CrewPermissionsService,
        @Inject("workplaceId") public workplaceId: number,
        @Inject("year") private year: number,
        @Inject("month") private month: number
    ) {
        
    }

    private readonly onDestroy = new Subject();

    isPermissionWriteOrAdmin: Observable<boolean>;

    persons: Observable<Nema.Crew.Model.Person[]>;

    workgroups: Observable<Nema.Crew.Model.WorkGroup[]>;

    activityTypes: Observable<Nema.Crew.Model.ActivityType[]>;

    workplaces: Observable<Nema.Crew.Model.Workplace[]>;

    selectedPersons: Nema.Crew.Model.Person[] = [];

    selectedActivityTypes: Nema.Crew.Model.ActivityType[] = [];

    fromDate: Date;

    toDate: Date;

    private isLoadingCount = 0;

    private setIsLoading(isLoading: boolean, count?: number): void {
        count = (count ?? 1) * (isLoading ? 1 : -1);
        this.isLoadingCount += count;
    }

    get isLoading(): boolean {
        return !!this.isLoadingCount;
    }

    ngOnInit(): void {
        this.setIsLoading(true, 5);

        this.isPermissionWriteOrAdmin = this.crewPermissionsService.isPermissionWriteOrAdmin().pipe(
            tap(() => this.setIsLoading(false)));

        this.persons = this.fetchBodyAndResetLoading(this.personService.getPersons());
        this.workplaces = this.fetchBodyAndResetLoading(this.workplaceService.getWorkplaces());
        this.workgroups = this.fetchBodyAndResetLoading(this.workgroupService.getWorkgroups());
        this.activityTypes = this.fetchBodyAndResetLoading(this.activityTypeService.getActivityTypes());

        const d = new Date();
        this.year = this.year ?? d.getFullYear();
        this.month = this.month ?? d.getMonth();
        this.fromDate = new Date(this.year, this.month, 1);
        this.toDate = new Date(this.year, this.month + 1, 0);
    }

    private fetchBodyAndResetLoading<T>(observable: Observable<HttpResponse<T>>): Observable<T> {
        return observable.pipe(
            map(res => res.body),
            tap(() => this.setIsLoading(false)));
    }

    getWorkGroupPersonIdSelector(activePersons: Nema.Crew.Model.Person[]): (workGroup: Nema.Crew.Model.WorkGroup) => number[] {
        // Map to person id and make sure that the person is active.
        return workGroup => workGroup.WorkGroupPersons.map(workGroupPerson => workGroupPerson.PersonId).filter(personId => activePersons.some(activePerson => activePerson.Id === personId));
    }

    clearActivities(): void {
        this.setIsLoading(true);

        const dto: ClearActivitiesDto = {
            personIds: this.selectedPersons.map(p => p.Id),
            activityTypeIds: this.selectedActivityTypes.map(at => at.Id),
            workplaceId: this.workplaceId,
            fromDate: dataCore.subtractTimezoneOffset(this.fromDate),
            toDate: dataCore.subtractTimezoneOffset(this.toDate)
        };

        this.activityService.clearActivities(dto)
            .pipe(
                tap(() => this.setIsLoading(false)),
                switchMap(() => this.stateService.go(
                    "app.activities.overview.with-params",
                    {
                        workplaceId: this.workplaceId,
                        workgroupId: -1,
                        year: this.fromDate.getFullYear(),
                        month: this.fromDate.getMonth() + 1
                    })),
                takeUntil(this.onDestroy))
            .subscribe();
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
    }
}

export interface ClearActivitiesDto {
    personIds: number[];
    activityTypeIds: number[];
    workplaceId: number;
    fromDate: Date;
    toDate: Date;
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/@angularclass/hmr-loader!./node_modules/tslint-loader!./client/app/activities/clear-activities.component.ts